import { GitCommitVertical } from 'lucide-react';
import {
  backorderStatusLabel,
  type BackorderReport,
  type BackorderStage,
  type PipelineReport,
  type PipelineStage,
} from '@eombor/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { Badge, cn } from '@eombor/ui';
import type { ApiClientError } from '../../../shared/api/client.js';
import { t } from '../../../shared/i18n/index.js';
import { requisitionStatusText } from '../../../shared/i18n/labels.js';
import { ReportCard } from './ReportCard.js';

/**
 * JARAYON VORONKASI — «so'rov qayerda tiqilib qoldi?».
 *
 * Halqa diagrammasi (`RequisitionStatusBlock`) taqsimotni ko'rsatadi,
 * lekin bosqichlarning TARTIBINI yo'qotadi va shu sababli tiqilish
 * joyini ko'rsata olmaydi. Voronkada bosqichlar oqim bo'ylab pastga
 * tushadi va bitta keng ustun darrov ko'zga tashlanadi.
 *
 * Har qatorda SONdan tashqari YOSH ham bor va ko'pincha muhimrog'i
 * o'sha: uchta hujjat turgan bosqich odatiy hol, ulardan biri qirq
 * kundan beri turgan bo'lsa — bu allaqachon muammo.
 *
 * «Olib ketilmagan» bu yerdan CHIQARILDI (`AwaitingPickupBlock`): u
 * panelning eng qimmat raqami va voronka ichidagi qator bo'lib
 * turganda boshqa bosqichlar bilan bir xil vaznda ko'rinardi. Bandlik
 * oralig'idagi bosqichlar bu yerda urg'u bilan qoladi — ular o'sha
 * kartochkaga ishora qiladi.
 */

const format = (value: number): string => value.toLocaleString('ru-RU');

/**
 * Kutish MUDDATI qachon ogohlantirishga aylanadi.
 *
 * Chegaralar qat'iy va shu yerda: ularsiz rang qiymatga nisbatan
 * tanlanardi («eng keksasi qizil») va bir kunlik navbatda ham kimdir
 * qizil bo'lib turardi — doim qizil turgan belgi o'qilmay qoladi.
 */
const AGE_WARN_DAYS = 7;
const AGE_DANGER_DAYS = 14;

const ageTone = (days: number): 'danger' | 'warn' | 'neutral' => {
  if (days >= AGE_DANGER_DAYS) return 'danger';
  if (days >= AGE_WARN_DAYS) return 'warn';
  return 'neutral';
};

const AGE_CLASS: Record<'danger' | 'warn' | 'neutral', string> = {
  danger: 'text-danger',
  warn: 'text-warn',
  neutral: 'text-text-faint',
};

interface StageRow {
  key: string;
  label: string;
  count: number;
  oldestDays: number | null;
  /** Bosqich «band qilingan, hali berilmagan» oraliqqa kiradimi. */
  awaiting: boolean;
}

/**
 * Bosqichlar ro'yxati — yorliq, ixcham ustun, yosh va son.
 *
 * Ustun QAT'IY enli (`w-24`) va qatorning butun enini olmaydi. Ilgari
 * u yorliq ostida to'la en bo'ylab chizilardi va sakkizta bosqichning
 * hammasida bir xil son turganda (odatiy hol) ekranda sakkizta bir xil
 * to'la chiziq paydo bo'lardi: ular ma'lumot emas, ajratuvchi chiziq
 * bo'lib o'qilardi va blokni shovqinga to'ldirardi.
 *
 * Ustun ENI eng katta bosqichga nisbatan: uning vazifasi «qaysi bosqich
 * og'irroq» degan savolga ko'z bilan javob berish, aniq sonni esa
 * yonidagi raqam aytadi.
 */
const StageList = ({ rows }: { rows: readonly StageRow[] }) => {
  const max = Math.max(1, ...rows.map((row) => row.count));

  return (
    <ul className="flex flex-col">
      {rows.map((row) => (
        <li
          key={row.key}
          className={cn(
            'flex items-center gap-3 py-1.5',
            /* Bo'sh bosqich SO'NIQ: u qatorda qoladi (tushib qolgan
               bosqich «bu yerda hech qachon hech narsa bo'lmaydi»
               degandek tuyuladi), lekin e'tibor talab qilmaydi. */
            row.count === 0 ? 'opacity-55' : undefined,
          )}
        >
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-sm',
              row.awaiting && row.count > 0 ? 'font-medium text-text' : 'text-text-muted',
            )}
          >
            {row.label}
          </span>

          <span aria-hidden className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-surface-2">
            <span
              className={cn(
                'block h-full rounded-full transition-[width] duration-[var(--motion-slow)]',
                row.awaiting ? 'bg-warn' : 'bg-accent',
              )}
              style={{ width: `${String(row.count === 0 ? 0 : Math.max(8, (row.count / max) * 100))}%` }}
            />
          </span>

          <span
            className={cn(
              'tabular w-14 shrink-0 text-right text-xs',
              row.oldestDays === null ? 'text-transparent' : AGE_CLASS[ageTone(row.oldestDays)],
            )}
          >
            {/* Bo'sh bosqichda ham joy BAND qoladi: sonlar ustuni
                qatordan qatorga sakramasligi kerak. */}
            {row.oldestDays === null ? '—' : t('report.pipeAge', { days: row.oldestDays })}
          </span>

          <span className="tabular w-8 shrink-0 text-right text-sm font-medium text-text">
            {format(row.count)}
          </span>
        </li>
      ))}
    </ul>
  );
};

const stageRowOf = (stage: PipelineStage, awaiting: boolean): StageRow => ({
  key: stage.status,
  label: requisitionStatusText(stage.status),
  count: stage.count,
  oldestDays: stage.oldestDays,
  awaiting,
});

const backorderRowOf = (stage: BackorderStage): StageRow => ({
  key: stage.status,
  label: backorderStatusLabel(stage.status),
  count: stage.count,
  oldestDays: stage.oldestDays,
  awaiting: false,
});

/** «Band qilingan, hali berilmagan» oraliq — serverdagi ro'yxat bilan bir xil. */
const AWAITING = ['WAREHOUSE_ACCEPTED', 'ACCOUNTING_PENDING', 'ACCOUNTING_APPROVED', 'READY_FOR_PICKUP'];

export interface PipelineBlockProps {
  query: UseQueryResult<PipelineReport, ApiClientError>;
  backorders: UseQueryResult<BackorderReport, ApiClientError>;
}

export const PipelineBlock = ({ query, backorders }: PipelineBlockProps) => {
  const stages = (query.data?.stages ?? []).map((stage) =>
    stageRowOf(stage, AWAITING.includes(stage.status)),
  );

  return (
    <ReportCard
      icon={GitCommitVertical}
      tone="warn"
      title={t('report.pipeTitle')}
      hint={t('report.pipeHint')}
      pending={query.isPending}
      error={query.error ?? null}
      empty={query.isSuccess && query.data.openCount === 0}
      emptyText={t('report.pipeEmpty')}
    >
      {/*
        IKKI USTUN: chapda talabnoma bosqichlari, o'ngda bildirgilar.
        Bildirgi talabnomaning bosqichi emas, undan OLDINGI so'rov —
        qoldiqda yo'q mahsulot uchun. Bitta ustunga terilganda ular
        bitta voronkaning davomidek o'qilardi.
      */}
      <div className="grid gap-5 lg:grid-cols-2 lg:gap-8">
        <section className="flex min-w-0 flex-col gap-2">
          <h3 className="text-xs font-semibold tracking-wide text-text-muted uppercase">
            {t('report.pipeStages')}
          </h3>
          <StageList rows={stages} />
        </section>

        <section className="flex min-w-0 flex-col gap-2">
          <h3 className="flex items-center gap-2 text-xs font-semibold tracking-wide text-text-muted uppercase">
            {t('report.pipeBackorders')}
            {backorders.isSuccess && backorders.data.openCount > 0 ? (
              <Badge tone="warn">{format(backorders.data.openCount)}</Badge>
            ) : null}
          </h3>
          {backorders.isError ? (
            <p className="text-sm text-text-muted">{t('report.failed')}</p>
          ) : backorders.isSuccess && backorders.data.openCount === 0 ? (
            <p className="text-sm text-text-faint">{t('report.pipeBackordersEmpty')}</p>
          ) : (
            <StageList rows={(backorders.data?.stages ?? []).map(backorderRowOf)} />
          )}
        </section>
      </div>
    </ReportCard>
  );
};
